import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { ScheduledReportResponse, ScheduledReportCreateRequest, ScheduledReportUpdateRequest, Schedule } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper, EnumToComboValues } from '../utils';
import { ScheduledReportDetailsSectionHandler } from './details.section';
import { ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { ReportListSectionHandler } from './reports.section';
import { ReportingService } from 'app/shared/components/reports';
import moment from 'moment-timezone';
import { getScheduleDescription } from './utils';
import { HealthIssueService } from '../healthissues/healthissue.service';
import { DomSanitizer } from '@angular/platform-browser';
import { NotificationService } from 'app/services';

@RegisterEntityHandler('scheduledreport')
@Injectable()
export class ScheduledReportHandler extends EntityHandler<ScheduledReportResponse> {

    constructor(
        admin: AdminEditorService,
        protected reportingService: ReportingService,
        public issues: HealthIssueService,
        public sanitizer: DomSanitizer,
        public notify: NotificationService
    ) {
        super(admin);
    }

    states = ['active', 'deleted'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listScheduledReports/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listScheduledReports(ownerId, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'scheduledreport',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.SCHEDULEDREPORTS'),
        }));
    }

    getEntity(id: string): Promise<ScheduledReportResponse> {
        return this.admin.getCached(`getScheduledReport/${id}`, api => api.entities.getScheduledReport(id), 10);
    }

    createEntity(ownerId: string, entity: ScheduledReportCreateRequest): Promise<ScheduledReportResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.entities.createScheduledReport(entity), { _type: 'scheduledreport' });
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.entities.updateScheduledReport(entity.id, { state: state as any }));
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.SCHEDULEDREPORT.COLUMNS');
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', ICONS.scheduledreport),
            {
                ...grid.TextColumn('schedule', 'SCHEDULE', 150),
                format: (col, row) => {
                    const item = row[col.field] as Schedule;
                    return getScheduleDescription(this.admin.i18n, this.admin.units, item);
                },
            },
            grid.TextColumn('reports', 'REPORTS', 60),
            grid.TextColumn('recipients', 'RECIPIENTS', 60),
            grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 80),
            grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
        ];
        return Promise.resolve(columns);
    }

    async getEditorSections(ownerId: string, entity: any): Promise<SectionHandlerGroup<ScheduledReportResponse>[]> {
        return [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new ScheduledReportDetailsSectionHandler(this.admin, ownerId, false),
                new ReportListSectionHandler(this.admin, ownerId, this.reportingService, this.notify),
            ],
            warnings: entity ? (await this.issues.getIssuesForEntity(ownerId, entity.id, 1)).map(issue => {
                return this.sanitizer.bypassSecurityTrustHtml(`${issue.description}<br><a href="/#/admin/client/${entity.owner.id}/healthissues/grid/healthissue/${issue.id}">More</a>`);
            }) : [],

        }];
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: ScheduledReportUpdateRequest, sections: FormSectionHandler<ScheduledReportResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'scheduledreport',
            entity: {
                user: {
                    id: this.admin.user.id,
                },
                schedule: {
                    scheduleType: 'once' as any,
                    startTime: moment().add(1, 'hour').utc().toISOString(),
                    every: 1,
                    days: [1],
                    months: [1],
                },
                recipients: [{ id: this.admin.user.id, name: this.admin.user.name, type: 'user' }],
            }, 
            sections: [
                new ScheduledReportDetailsSectionHandler(this.admin, ownerId, true),
            ],
        });
    }

}












